import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  IconButton,
  Avatar,
  Tooltip,
  ButtonBase,
  Typography,
  Snackbar,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  HelpOutlineOutlined,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { useTheme } from '../context/ThemeContext';
import { useAppReady } from '../context/AppReadyContext';
import { canAccessRoutePath } from '../utils/getDefaultAccessiblePath';
import { canAccessMesttiOps } from '../utils/permissionAccess';
import { TabManager } from './TabManager/TabManager';
import { isTabKeepAliveEnabled, getSidebarZIndex, getHeaderOverlayZIndex, APP_HEADER_HEIGHT_PX, LAYOUT_CHROME_TOP_CSS_VAR, LAYOUT_DRAWER_WIDTH_CSS_VAR, LAYOUT_OPS_DOCK_CSS_VAR } from '../config/tabKeepAlive';
import {
  ConsultoriaShellBackdrop,
} from '../../consultoria/components/ConsultoriaShellBackdrop';
import {
  isConsultoriaEnabled,
  useConsultoriaChat,
  OPS_DOCK_OUTER_PX,
  OPS_SHELL_GAP_PX,
} from '../../consultoria';
import { octoRadius } from '../../consultoria/theme/octoTokens';
import OctoplusLauncher from '../../consultoria/shell/OctoplusLauncher';
import { NotificationBell } from '../../notifications/components/NotificationBell';
import { NotificationPanel } from '../../notifications/components/NotificationPanel';
import { NotificationToastContainer } from '../../notifications/components/NotificationToastContainer';
import { buildManualPath } from '../../manual/utils/manualTopicPersistence';
import { ADMIN_PANEL_PATH, filterNavModules, orderNavModules, sortPinnedFirst } from '../navigation/navigationMenu';
import { useSidebarOrder } from '../navigation/useSidebarOrder';
import { useTabManager } from '../context/TabManagerContext';
import { AppSidebar, SIDEBAR_COLLAPSED_WIDTH_PX, SIDEBAR_WIDTH_PX } from '../navigation/AppSidebar';
import { AccountMenu, getInitials } from '../navigation/AccountMenu';
import { GoToDialog, GoToEntry } from '../navigation/GoToDialog';
import { getNavTokens, navFocusRing } from '../navigation/navTokens';
import { preferencesService } from '../../auth/service/preferencesService';
import { subscribePreferencesChanged } from '../../auth/service/preferencesEvents';
import { createPreferenceSync, PreferenceSync } from '../utils/navigationSync';

const ConsultoriaChatPanel = React.lazy(() =>
  import('../../consultoria/components/ConsultoriaChatPanel').then((m) => ({
    default: m.ConsultoriaChatPanel,
  })),
);

/** Mesmo raio do painel do Otto, para a folha acompanhar quando ele está acoplado. */
const OPS_PANEL_RADIUS_PX = octoRadius.panel;

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'mestti.sidebar.collapsed';

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const WEEKDAYS_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MONTHS_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const formatShortDateTime = (date: Date) =>
  `${WEEKDAYS_SHORT[date.getDay()]}, ${date.getDate()} ${MONTHS_SHORT[date.getMonth()]} · ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;

const formatLongDateTime = (date: Date) =>
  date.toLocaleString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** Data e hora discretas; a forma completa fica no tooltip. */
const HeaderClock = React.memo(({ color }: { color: string }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Tooltip title={formatLongDateTime(now)}>
      <Typography
        component="time"
        dateTime={now.toISOString()}
        noWrap
        sx={{ fontSize: '0.8125rem', color, fontVariantNumeric: 'tabular-nums', px: 1 }}
      >
        {formatShortDateTime(now)}
      </Typography>
    </Tooltip>
  );
});

interface LayoutProps {
  children: React.ReactNode;
}

const LayoutContent: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const [goToOpen, setGoToOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const sidebarOrder = useSidebarOrder(user?.id);
  const { hasPermission, mesttiOpsAccess } = usePermissions();
  const { isSuperAdmin } = useAccessControl();
  const { setAppReady } = useAppReady();
  const { isDarkMode, setThemeMode } = useTheme();
  // Tema pelo menu da conta: aplica na hora e grava nas preferências do usuário (mesmo
  // lugar da tela Preferências), em série e só a escolha mais recente; se ela falhar,
  // volta ao último tema confirmado e avisa (ver createPreferenceSync).
  const [themeSaveFailed, setThemeSaveFailed] = useState(false);
  const setThemeModeRef = useRef(setThemeMode);
  setThemeModeRef.current = setThemeMode;
  const syncThemeRef = useRef<PreferenceSync<'light' | 'dark'> | null>(null);
  if (!syncThemeRef.current) {
    syncThemeRef.current = createPreferenceSync<'light' | 'dark'>({
      persist: (mode) => preferencesService.updatePreferences({ tema: mode === 'dark' ? 1 : 0 }, 'account-menu'),
      apply: (mode) => setThemeModeRef.current(mode),
      onRollback: () => setThemeSaveFailed(true),
    });
  }
  const handleToggleTheme = () => {
    setThemeSaveFailed(false);
    syncThemeRef.current?.set(isDarkMode ? 'light' : 'dark', isDarkMode ? 'dark' : 'light');
  };
  // Tema gravado por outro caminho (tela Preferências, outra aba do navegador): vira o
  // ponto de retorno, para uma falha posterior aqui não voltar a um tema antigo.
  useEffect(() => subscribePreferencesChanged((sourceId) => {
    if (sourceId === 'account-menu') return;
    const confirm = syncThemeRef.current?.beginExternalRead();
    preferencesService.getPreferences()
      .then((prefs) => confirm?.(prefs.tema === 1 ? 'dark' : 'light'))
      .catch(() => { /* Sem servidor, mantém o ponto de retorno atual. */ });
  }), []);
  const t = getNavTokens(useMuiTheme());
  const { openPanel, minimizeShell, isPanelOpen, isPanelExpanded } = useConsultoriaChat();
  const showConsultoriaChats =
    isConsultoriaEnabled() && canAccessMesttiOps(mesttiOpsAccess);
  /** Docked (non-expanded) chat pushes the SaaS main column. */
  const opsDockPx =
    showConsultoriaChats && isPanelOpen && !isPanelExpanded ? OPS_DOCK_OUTER_PX : 0;

  // No :root para valer também em overlays portados no body (ex.: SidePanel).
  useEffect(() => {
    document.documentElement.style.setProperty(LAYOUT_OPS_DOCK_CSS_VAR, `${opsDockPx}px`);
  }, [opsDockPx]);

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const drawerWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH_PX : SIDEBAR_WIDTH_PX;
  const companyId = (user as any)?.company_id || (user as any)?.companyId || '';

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, prev ? '0' : '1');
      } catch {
        // Só conveniência: sem storage o menu apenas não lembra o estado.
      }
      return !prev;
    });
  };

  // Celular: o painel do Octoplus cobre a tela; abrir o menu esconde o chat (a conversa e o
  // rascunho ficam guardados) para o menu não abrir por trás do painel.
  useEffect(() => {
    if (isMobile && mobileOpen && isPanelOpen) minimizeShell();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileOpen]);

  // Ao abrir o Octoplus o menu lateral NÃO muda: o painel se acopla à direita e só a
  // coluna de conteúdo encolhe. No celular o painel cobre a tela, então o drawer fecha.
  useEffect(() => {
    if (showConsultoriaChats && isPanelOpen) setMobileOpen(false);
  }, [isPanelOpen, showConsultoriaChats]);

  // Sinalizar pronto quando o layout realmente montou e pintou.
  useEffect(() => {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setAppReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [setAppReady]);

  // Ctrl/Cmd + K abre a busca de páginas de qualquer tela.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setGoToOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const canOpen = useCallback(
    (path: string) => canAccessRoutePath(path, hasPermission, isSuperAdmin),
    [hasPermission, isSuperAdmin],
  );
  const { tabs, activeTabId, togglePagePin } = useTabManager();
  const [isDraggingFirstTab, setIsDraggingFirstTab] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const updateDragCorner = useCallback((radius: number | null) => {
    const style = contentRef.current?.style;
    if (!style) return;
    if (radius === null) style.removeProperty('--tab-drag-corner');
    else style.setProperty('--tab-drag-corner', `${radius}px`);
  }, []);
  // Com a primeira aba ativa, ela desce reta na borda do conteúdo; senão, o canto do
  // conteúdo junto à sidebar é arredondado (mesmo raio das abas).
  const firstTab = tabs[0];
  const firstTabActive = Boolean(
    firstTab && (firstTab.path === location.pathname || (!tabs.some((tab) => tab.path === location.pathname) && firstTab.id === activeTabId)),
  );
  const pinnedKey = JSON.stringify(
    tabs.filter((tab) => tab.isPinned).map((tab) => ({ label: tab.label, path: tab.path })),
  );
  const pinnedPages = useMemo(
    () => sortPinnedFirst(JSON.parse(pinnedKey) as { label: string; path: string }[], new Set<string>()),
    [pinnedKey],
  );
  const pinnedPaths = useMemo(() => new Set(pinnedPages.map((page) => page.path)), [pinnedPages]);
  const navModules = useMemo(
    () => orderNavModules(filterNavModules(hasPermission, isSuperAdmin, pinnedPaths), sidebarOrder),
    [hasPermission, isSuperAdmin, pinnedPaths, sidebarOrder],
  );
  const showAdmin = canOpen(ADMIN_PANEL_PATH);
  const canOpenSettings = canOpen('/settings');

  const goToEntries = useMemo<GoToEntry[]>(() => {
    const pages = navModules.flatMap((module) =>
      module.pages.map((page) => ({ label: page.label, context: module.label, path: page.path })),
    );
    // Fixadas fora dos módulos (ex.: Dashboard) também entram na busca.
    pinnedPages
      .filter((pinned) => canOpen(pinned.path) && !pages.some((page) => page.path === pinned.path))
      .forEach((pinned) => pages.push({ ...pinned, context: '' }));
    if (canOpenSettings) pages.push({ label: 'Configurações', context: 'Empresa', path: '/settings' });
    if (showAdmin) pages.push({ label: 'Painel Admin', context: 'Plataforma', path: ADMIN_PANEL_PATH });
    pages.push({ label: 'Perfil e segurança', context: 'Conta', path: '/profile' });
    pages.push({ label: 'Manual', context: 'Ajuda', path: buildManualPath() });
    return sortPinnedFirst(pages, pinnedPaths).map((page) => ({ ...page, pinned: pinnedPaths.has(page.path) }));
  }, [navModules, canOpenSettings, showAdmin, pinnedPages, pinnedPaths, canOpen]);

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const openSearch = () => {
    setMobileOpen(false);
    setGoToOpen(true);
  };

  const actionButtonSx = {
    width: 36,
    height: 36,
    color: t.icon,
    '&:hover': { backgroundColor: t.hover, color: t.ink },
    ...navFocusRing(t),
  };

  const mobileFooterItemSx = {
    height: 40,
    borderRadius: '8px',
    justifyContent: 'flex-start',
    px: 1.5,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: t.ink,
    '&:hover': { backgroundColor: t.hover },
    ...navFocusRing(t),
  };

  const mobileFooter = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', mt: 1 }}>
      {showConsultoriaChats && (
        <ButtonBase onClick={() => { openPanel(); setMobileOpen(false); }} sx={mobileFooterItemSx}>Otto</ButtonBase>
      )}
      <ButtonBase onClick={() => handleNavigation(buildManualPath())} sx={mobileFooterItemSx}>Manual</ButtonBase>
      <Box sx={{ px: 1.5, pt: 1 }}><HeaderClock color={t.muted} /></Box>
    </Box>
  );

  const sidebarProps = {
    modules: sidebarOrder === null ? [] : navModules,
    modulesLoading: sidebarOrder === null,
    pinnedPaths,
    pathname: location.pathname,
    settingsPath: canOpenSettings ? '/settings' : '/profile',
    showAdmin,
    onNavigate: handleNavigation,
    onTogglePin: togglePagePin,
    onOpenSearch: openSearch,
  };

  const drawerPaperSx = {
    boxSizing: 'border-box' as const,
    border: 'none',
    backgroundImage: 'none',
    backgroundColor: t.chrome,
    overflowX: 'hidden' as const,
    ...(isTabKeepAliveEnabled() && {
      zIndex: (theme: any) => getSidebarZIndex(theme),
    }),
  };

  return (
    <Box
      data-mestti-app-shell
      sx={{
        display: 'flex',
        bgcolor: t.chrome,
        [LAYOUT_DRAWER_WIDTH_CSS_VAR]: {
          xs: '0px',
          sm: `${drawerWidth}px`,
        },
        [LAYOUT_CHROME_TOP_CSS_VAR]: `${APP_HEADER_HEIGHT_PX}px`,
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          height: APP_HEADER_HEIGHT_PX,
          backgroundColor: t.chrome,
          backgroundImage: 'none',
          color: t.icon,
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          pl: { xs: 0.5, sm: 0 },
          pr: { xs: 1, sm: 1.5 },
          ...(isTabKeepAliveEnabled() && {
            zIndex: (theme) => getHeaderOverlayZIndex(theme),
          }),
        }}
      >
        <IconButton
          aria-label="Abrir menu"
          onClick={() => setMobileOpen(true)}
          sx={{ ...actionButtonSx, width: 44, height: 44, display: { xs: 'flex', sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <TabManager onDraggingFirstChange={setIsDraggingFirstTab} onDragCornerChange={updateDragCorner} />

        {/* Mesma linha das abas (base da barra); fica sempre no canto direito, mesmo com o Otto aberto. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flex: 'none', ml: 'auto', alignSelf: { sm: 'flex-end' }, height: { sm: 38 }, mb: { sm: '4px' } }}>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <HeaderClock color={t.muted} />
          </Box>
          <Tooltip title="Manual">
            <IconButton
              aria-label="Manual"
              onClick={() => navigate(buildManualPath())}
              sx={{ ...actionButtonSx, display: { xs: 'none', sm: 'inline-flex' } }}
            >
              <HelpOutlineOutlined sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          {showConsultoriaChats && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <OctoplusLauncher headerText={t.icon} headerHoverBg={t.hover} />
            </Box>
          )}
          <NotificationBell
            companyId={companyId}
            userId={(user as any)?.id}
            onClick={() => setIsNotifOpen((open) => !open)}
            sx={actionButtonSx}
          />
          <Tooltip title="Conta">
            <IconButton
              aria-label="Conta"
              aria-haspopup="true"
              aria-expanded={Boolean(accountAnchor)}
              onClick={(event) => setAccountAnchor(event.currentTarget)}
              sx={actionButtonSx}
            >
              <Avatar sx={{ width: 30, height: 30, bgcolor: '#3A6CA8', color: '#FFFFFF', fontSize: 12.5, fontWeight: 600 }}>
                {getInitials(user?.full_name || user?.email)}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </AppBar>

      <AccountMenu
        anchorEl={accountAnchor}
        onClose={() => setAccountAnchor(null)}
        name={user?.full_name}
        email={user?.email}
        companyName={user?.company_name}
        userLevel={user?.user_level}
        isDarkMode={isDarkMode}
        onToggleTheme={handleToggleTheme}
        onNavigate={handleNavigation}
        onLogout={logout}
      />

      <Box
        component="div"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 }, transition: 'width 200ms ease' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { ...drawerPaperSx, width: 300, borderRadius: '0 20px 20px 0' },
          }}
        >
          <AppSidebar {...sidebarProps} collapsed={false} footer={mobileFooter} />
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { ...drawerPaperSx, width: drawerWidth, transition: 'width 200ms ease' },
          }}
        >
          <AppSidebar {...sidebarProps} collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
        </Drawer>
      </Box>

      <Box
        component="main"
        ref={contentRef}
        sx={{
          flexGrow: 1,
          p: 0,
          width: {
            sm: `calc(100% - ${drawerWidth}px - ${opsDockPx}px)`,
          },
          mr: { sm: opsDockPx ? `${opsDockPx}px` : 0 },
          transition: 'width 220ms cubic-bezier(0.22,1,0.36,1), margin 220ms cubic-bezier(0.22,1,0.36,1)',
          mt: `${APP_HEADER_HEIGHT_PX}px`,
          // Altura da janela abaixo do cabeçalho: quem rola é a área de conteúdo (abaixo), não a
          // página. Sem isto a barra de rolagem da página aparecia e sumia conforme o conteúdo.
          height: `calc(100dvh - ${APP_HEADER_HEIGHT_PX}px)`,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          position: 'relative',
          backgroundColor: t.canvas,
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '0 0 auto',
            height: 13,
            zIndex: 1,
            pointerEvents: 'none',
            backgroundImage: `linear-gradient(
              to bottom,
              ${t.canvas} 2px,
              ${alpha(t.canvas, 0.62)} 45%,
              ${alpha(t.canvas, 0.22)} 75%,
              ${alpha(t.canvas, 0)}
            )`,
          },
          borderTopLeftRadius: { sm: `var(--tab-drag-corner, ${firstTabActive && !isDraggingFirstTab ? 0 : 10}px)` },
          // Com o Otto acoplado, a folha vira um bloco igual ao dele: mesma base e cantos.
          ...(opsDockPx && {
            height: { sm: `calc(100dvh - ${APP_HEADER_HEIGHT_PX + OPS_SHELL_GAP_PX}px)` },
            mb: { sm: `${OPS_SHELL_GAP_PX}px` },
            borderTopRightRadius: { sm: OPS_PANEL_RADIUS_PX },
            borderBottomRightRadius: { sm: OPS_PANEL_RADIUS_PX },
            borderBottomLeftRadius: { sm: OPS_PANEL_RADIUS_PX },
          }),
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          px: { xs: 1.5, md: 3 },
          pb: { xs: 1.5, md: 3 },
          pt: { xs: 0.75, md: 1.5 },
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          // Barra de rolagem no tom da folha: trilho transparente, polegar discreto.
          scrollbarWidth: 'thin',
          scrollbarColor: `${t.hover} transparent`,
          '&::-webkit-scrollbar': { width: 8, backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: t.hover, borderRadius: 4 },
          '&:hover::-webkit-scrollbar-thumb': { backgroundColor: t.muted },
        }}>
          {children}
        </Box>
      </Box>

      <Snackbar
        open={themeSaveFailed}
        autoHideDuration={5000}
        onClose={() => setThemeSaveFailed(false)}
        message="Não foi possível salvar o tema. Ele voltou ao anterior."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <GoToDialog
        open={goToOpen}
        entries={goToEntries}
        onClose={() => setGoToOpen(false)}
        onNavigate={handleNavigation}
      />

      {/* Mestti OPS Chats — heavy panel is code-split; only mounts when flag+permission allow. */}
      {showConsultoriaChats && (
        <React.Suspense fallback={null}>
          <ConsultoriaShellBackdrop />
          <ConsultoriaChatPanel />
        </React.Suspense>
      )}

      <NotificationPanel
        open={isNotifOpen}
        onClose={() => setIsNotifOpen(false)}
        companyId={companyId}
        userId={(user as any)?.id}
      />

      <NotificationToastContainer
        companyId={companyId}
        userId={(user as any)?.id}
      />

    </Box>
  );
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  return <LayoutContent>{children}</LayoutContent>;
};
